export const CHAT_ACCESS_TOKEN_UNAVAILABLE = 'CHAT_ACCESS_TOKEN_UNAVAILABLE';

export class RecoverableChatAuthError extends Error {
  code = CHAT_ACCESS_TOKEN_UNAVAILABLE;

  constructor(message = 'Chat access token is temporarily unavailable') {
    super(message);
    this.name = 'RecoverableChatAuthError';
  }
}

type ErrorRecord = Record<string, unknown>;

interface ChatErrorSignals {
  messages: string[];
  names: string[];
  codes: string[];
  statuses: number[];
}

const isRecord = (value: unknown): value is ErrorRecord =>
  typeof value === 'object' && value !== null;

const pushString = (values: string[], value: unknown) => {
  if (typeof value === 'string' && value.trim()) {
    values.push(value);
  }
};

const pushStatus = (values: number[], value: unknown) => {
  if (typeof value === 'number') {
    values.push(value);
  }
};

const tryParseJson = (value: string): unknown | null => {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
};

const parseEmbeddedJson = (message: string): unknown | null => {
  const firstBraceIndex = message.indexOf('{');

  if (firstBraceIndex === -1) {
    return null;
  }

  return tryParseJson(message.slice(firstBraceIndex));
};

const collectSignals = (error: unknown, signals: ChatErrorSignals, seen: Set<unknown>): void => {
  if (error === null || error === undefined || seen.has(error)) {
    return;
  }

  seen.add(error);

  if (typeof error === 'string') {
    signals.messages.push(error);

    const parsedError = tryParseJson(error) ?? parseEmbeddedJson(error);
    if (parsedError) {
      collectSignals(parsedError, signals, seen);
    }

    return;
  }

  if (!isRecord(error)) {
    signals.messages.push(String(error));
    return;
  }

  pushString(signals.messages, error.message);
  pushString(signals.messages, error.errorType);
  pushString(signals.names, error.name);
  pushString(signals.names, error.errorType);
  pushString(signals.codes, error.code);
  pushStatus(signals.statuses, error.status);

  if (isRecord(error.response)) {
    pushStatus(signals.statuses, error.response.status);
    collectSignals(error.response.data, signals, seen);
  }

  if (Array.isArray(error.errors)) {
    error.errors.forEach(nestedError => collectSignals(nestedError, signals, seen));
  }

  collectSignals(error.error, signals, seen);
  collectSignals(error.cause, signals, seen);
  collectSignals(error.data, signals, seen);
  collectSignals(error.errorInfo, signals, seen);

  const message = typeof error.message === 'string' ? error.message : '';
  const parsedMessage = message ? (tryParseJson(message) ?? parseEmbeddedJson(message)) : null;

  if (parsedMessage) {
    collectSignals(parsedMessage, signals, seen);
  }
};

export const extractChatErrorSignals = (error: unknown): ChatErrorSignals => {
  const signals: ChatErrorSignals = {
    messages: [],
    names: [],
    codes: [],
    statuses: []
  };

  collectSignals(error, signals, new Set());
  return signals;
};

const anySignalMatches = (error: unknown, predicate: (signal: string) => boolean): boolean => {
  const { messages, names, codes } = extractChatErrorSignals(error);

  return [...messages, ...names, ...codes].some(predicate);
};

export const createRecoverableChatAuthError = () => new RecoverableChatAuthError();

export const isRecoverableChatAuthError = (error: unknown): boolean =>
  anySignalMatches(
    error,
    signal =>
      signal === CHAT_ACCESS_TOKEN_UNAVAILABLE ||
      signal === 'RecoverableChatAuthError' ||
      /jwt token is required/i.test(signal)
  );

export const isDuplicatedOperationError = (error: unknown): boolean =>
  anySignalMatches(
    error,
    signal => /DuplicatedOperationError/i.test(signal) || /Duplicated operation/i.test(signal)
  );

export const isMaxSubscriptionsReachedError = (error: unknown): boolean =>
  anySignalMatches(error, signal => /MaxSubscriptionsReachedError/i.test(signal));

export const isServerUnauthorizedError = (error: unknown): boolean => {
  if (isRecoverableChatAuthError(error)) {
    return false;
  }

  const { statuses } = extractChatErrorSignals(error);

  return (
    statuses.some(status => status === 401 || status === 403) ||
    anySignalMatches(
      error,
      signal =>
        /UnauthorizedException/i.test(signal) ||
        /not authorized/i.test(signal) ||
        /\bunauthorized\b/i.test(signal)
    )
  );
};

export const isTerminalRefreshAuthError = (error: unknown): boolean => {
  if (isRecoverableChatAuthError(error)) {
    return false;
  }

  return anySignalMatches(
    error,
    signal =>
      signal === 'TerminalChatAuthError' ||
      signal === 'CHAT_REFRESH_STATE_UNAVAILABLE' ||
      signal === 'CHAT_TERMINAL_REFRESH_FAILURE'
  );
};

export const isHardChatAuthFailure = (error: unknown): boolean =>
  isServerUnauthorizedError(error) || isTerminalRefreshAuthError(error);
